import type { ChangeEvent } from 'react';
import { Box, Input } from '@chakra-ui/react';
import { Button } from '@kula-ai/components';
import { Plus } from '@kula-ai/icons-core';
import { useShowToast } from '@/hooks';
import { useUploadAttachmentMutation } from '../../api';
import { useOfferStore } from '../../store';

// Uploads an extra document onto this offer (additional_documents), same behaviour
// as the old footer button — relocated under the document tabs per Figma.
export const AddMoreFilesButton = ({
  offerId,
  variant = 'tertiary',
}: {
  offerId?: number | string;
  variant: string;
}) => {
  const addOfferAttachment = useOfferStore((state) => state.addOfferAttachment);
  const storeOfferId = useOfferStore((state) => state.offerId);
  const toast = useShowToast();

  const uploadMutation = useUploadAttachmentMutation({
    onSuccess: (data: { data?: { attachments?: unknown[] } }) => {
      const attachment = data?.data?.attachments?.[0];
      if (attachment) addOfferAttachment(attachment);
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to upload file',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadMutation.mutate({ id: offerId || storeOfferId || 0, file });
    event.target.value = '';
  };

  return (
    <Box position="relative" w="148px" cursor="pointer">
      <Input
        type="file"
        onChange={handleChange}
        position="absolute"
        top="0"
        left="0"
        w="100%"
        h="100%"
        opacity={0}
        cursor="pointer"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.odt"
        zIndex={1}
      />
      <Button
        variant={variant}
        size="regular"
        isLoading={uploadMutation.isPending}
        pointerEvents="none"
        leftElement={<Plus size={16} />}
      >
        Add more files
      </Button>
    </Box>
  );
};
